/**
 * Deity acquisition engine — the point system behind the STEP→TODAY→AIM→GOAL
 * ladder. Pure functions over a completion log; nothing here touches the DB or
 * React. The log is the single source of truth — every point is recomputed from
 * it, so banked points are never lost by a broken streak (breaking a streak
 * only stalls the fat AIM/GOAL bonuses and slides the projected date; it is
 * additive, not punitive).
 *
 * Point values (each rung pays ON TOP of the lower ones):
 *   STEP  +1   — one logged completion of one principle
 *   TODAY +5   — a day you showed up (≥1 completion)
 *   AIM   +30  — a principle that hit its weekly frequency, per week  ← the money
 *   GOAL  +150 — a principle held on-frequency for 4 consecutive weeks
 *
 * Acquire the deity at ACQUIRE_POINTS. Steps alone top out low, so the AIM/GOAL
 * bonuses force real consistency — you cannot grind your way there. Stacking
 * multiple principles multiplies steps AND aims, so it reaches the threshold far
 * faster (harder: you must keep them all alive).
 */
import type { PrincipleDef } from './deity-data';

/** Cumulative points to COMPLETE each level. L1 (400) = basic acquisition; each
 *  higher level costs more and re-populates the pick list with advanced usage
 *  principles. Banked points never reset — you keep climbing the same total. */
export const LEVEL_THRESHOLDS = [400, 700, 1000, 1300, 1600] as const;
/** Level 1 threshold — the "acquired" bar (kept as a named export). */
export const ACQUIRE_POINTS = LEVEL_THRESHOLDS[0];
export const PTS = { STEP: 1, TODAY: 5, AIM: 30, GOAL: 150 } as const;

/** Level (1-based) you're currently working toward + its point window. */
export function levelInfo(points: number): { level: number; floor: number; target: number; maxed: boolean } {
  let idx = 0;
  while (idx < LEVEL_THRESHOLDS.length && points >= LEVEL_THRESHOLDS[idx]) idx += 1;
  const maxed = idx >= LEVEL_THRESHOLDS.length;
  const level = maxed ? LEVEL_THRESHOLDS.length : idx + 1;
  const floor = idx > 0 ? LEVEL_THRESHOLDS[idx - 1] : 0;
  const target = LEVEL_THRESHOLDS[Math.min(idx, LEVEL_THRESHOLDS.length - 1)];
  return { level, floor, target, maxed };
}
/** Consecutive on-frequency weeks that bank one GOAL. */
const GOAL_WEEKS = 4;

/** Per-deity persisted progress: active principle picks + the completion log,
 *  both keyed by stable principle id (`toolId:levelIdx:principleIdx`). */
export interface DeityProgress {
  /** Active principle ids. */
  picks: string[];
  /** 'YYYY-MM-DD' → principle ids completed that day. */
  log: Record<string, string[]>;
}

export const EMPTY_PROGRESS: DeityProgress = { picks: [], log: {} };

/** A challenge the app issues (deterministic per period — not a user setting). */
export interface ChallengeState {
  label: string;
  detail: string;
  /** Bonus points when met (0 for a bypass-granting special). */
  bonus: number;
  /** True once its condition is satisfied. */
  met: boolean;
  /** A special challenge grants a bypass token instead of points. */
  grantsBypass?: boolean;
}

export interface ProgressStats {
  points: number;
  steps: number;
  aims: number;
  goals: number;
  /** Distinct days with at least one completion. */
  todayCount: number;
  /** 0 STEP · 1 TODAY · 2 AIM · 3 GOAL · 4 SECURED — drives the front dots. */
  tierIdx: number;
  /** True once Level 1 (basic acquisition) is reached. */
  secured: boolean;
  /** Current level (1-based) being worked toward. */
  level: number;
  /** Cumulative points that complete the current level. */
  levelTarget: number;
  /** Cumulative points at the start of the current level. */
  levelFloor: number;
  /** True at the top defined level. */
  maxed: boolean;
  /** 0..1 fill within the current level (floor→target). */
  pct: number;
  /** Average points/day since the first log (drops when days pass idle). */
  ratePerDay: number;
  /** Human date the deity is acquired at current pace, or null if unknowable. */
  projectedDate: string | null;
  /** When the current 4-month season ends and un-banked points refresh. */
  seasonEnd: string | null;
  /** Principle ids already logged today. */
  doneToday: string[];
  /** Active pick ids for the current level (picks ∩ current-level ids). */
  activePicks: string[];
  /** Total bonus points from met daily + weekly challenges (already in points). */
  challengeBonus: number;
  /** Bypass tokens remaining per tier (earned − auto-spent). */
  bypass: { day: number; week: number; goal: number };
  /** Today's app-issued challenge (null if no picks yet). */
  todayChallenge: ChallengeState | null;
  /** This week's app-issued challenge. */
  weekChallenge: ChallengeState | null;
  /** A rare special challenge, only present the weeks it's offered. */
  specialChallenge: ChallengeState | null;
}

// ── Challenge banks — the app issues one daily + one weekly per deity, picked
//    deterministically by a date/week hash so they're stable across renders. A
//    rare special challenge grants a bypass token (excuses a near-missed week).

interface DailyDef {
  label: string;
  detail: string;
  bonus: number;
  test: (dayIds: string[], picks: string[]) => boolean;
}
interface WeeklyDef {
  label: string;
  detail: string;
  bonus: number;
  test: (w: WeekStat) => boolean;
}
interface WeekStat {
  total: number;
  days: number;
  picksCount: number;
  allMet: boolean;
}

const DAILY_CHALLENGES: DailyDef[] = [
  { label: 'Clean sweep', detail: 'Do all your picks today', bonus: 8, test: (d, p) => p.length > 0 && p.every((i) => d.includes(i)) },
  { label: 'Triple threat', detail: 'Log 3 today', bonus: 8, test: (d) => d.length >= 3 },
  { label: 'Double up', detail: 'Log 2 today', bonus: 5, test: (d) => d.length >= 2 },
  { label: 'Break the seal', detail: 'Log at least 1 today', bonus: 3, test: (d) => d.length >= 1 },
];

const WEEKLY_CHALLENGES: WeeklyDef[] = [
  { label: 'Perfect week', detail: 'Every pick hits its frequency', bonus: 40, test: (w) => w.picksCount > 0 && w.allMet },
  { label: 'Grinder', detail: '20 reps this week', bonus: 25, test: (w) => w.total >= 20 },
  { label: 'Steady hand', detail: 'Show up 5 days this week', bonus: 20, test: (w) => w.days >= 5 },
];

/** Tiered special challenges — each grants a bypass token at its cadence level.
 *  A week offers at most one, rarest tier first (goal ⊂ week ⊂ day by hash). */
export type BypassTier = 'day' | 'week' | 'goal';

const SPECIALS: Record<BypassTier, { label: string; detail: string; icon: string; test: (w: WeekStat) => boolean }> = {
  day: {
    label: 'Momentum',
    detail: 'Show up 4 days this week — banks a 🩹 day bypass',
    icon: '🩹',
    test: (w) => w.days >= 4,
  },
  week: {
    label: 'Full week',
    detail: 'Every pick on-frequency — banks a 🛡 week bypass',
    icon: '🛡',
    test: (w) => w.picksCount > 0 && w.allMet,
  },
  goal: {
    label: 'Iron week',
    detail: 'Every pick on-frequency + 6 days — banks a 👑 goal bypass',
    icon: '👑',
    test: (w) => w.picksCount > 0 && w.allMet && w.days >= 6,
  },
};

/** Which bypass tier (if any) this week offers, rarest-first. */
function offeredTier(wk: number, seed: string): BypassTier | null {
  const h = hashStr(`s:${wk}:${seed}`);
  if (h % 8 === 0) return 'goal';
  if (h % 4 === 0) return 'week';
  if (h % 2 === 0) return 'day';
  return null;
}

/** FNV-1a — deterministic string→uint32 for stable per-period challenge picks. */
function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** AIM = each (id, week) meeting frequency; GOAL = every GOAL_WEEKS consecutive
 *  qualifying weeks. WEEK bypasses rescue the closest short weeks; GOAL bypasses
 *  bridge single-week gaps in a run. Mutates `bypass` (spends tokens). */
function aimsGoals(
  byPickWeek: Map<string, Map<number, number>>,
  perWeekOf: (id: string) => number,
  bypass: { week: number; goal: number },
): { aims: number; goals: number } {
  let aims = 0;
  let goals = 0;
  for (const [id, weeks] of byPickWeek) {
    const perWeek = perWeekOf(id);
    const qualifying: number[] = [];
    const short: number[] = [];
    for (const [wk, count] of weeks) {
      if (count >= perWeek) qualifying.push(wk);
      else short.push(wk);
    }
    short.sort((a, b) => (perWeek - (weeks.get(b) ?? 0)) - (perWeek - (weeks.get(a) ?? 0)));
    while (bypass.week > 0 && short.length) {
      bypass.week -= 1;
      qualifying.push(short.pop() as number);
    }
    aims += qualifying.length;
    qualifying.sort((a, b) => a - b);
    let run = 0;
    for (let i = 0; i < qualifying.length; i++) {
      if (i === 0) run = 1;
      else {
        const gap = qualifying[i] - qualifying[i - 1];
        if (gap === 1) run += 1;
        else if (gap === 2 && bypass.goal > 0) {
          bypass.goal -= 1;
          run += 2;
        } else run = 1;
      }
      while (run >= GOAL_WEEKS) {
        goals += 1;
        run -= GOAL_WEEKS;
      }
    }
  }
  return { aims, goals };
}

const MS_DAY = 24 * 3600 * 1000;
const MONDAY_EPOCH = Date.UTC(1970, 0, 5); // Mon 5 Jan 1970
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Parse 'YYYY-MM-DD' at noon UTC (TZ-safe). Returns null on malformed input. */
function parseDate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
}

/** Monday-based week ordinal — consecutive weeks differ by exactly 1. */
function weekOrdinal(d: Date): number {
  return Math.floor((d.getTime() - MONDAY_EPOCH) / (7 * MS_DAY));
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / MS_DAY);
}

function fmtDate(d: Date, withYear: boolean): string {
  const base = `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  return withYear ? `${base}, ${d.getUTCFullYear()}` : base;
}

/** 4-month season ordinal (UTC): Jan–Apr, May–Aug, Sep–Dec. */
function seasonOf(d: Date): number {
  return d.getUTCFullYear() * 3 + Math.floor(d.getUTCMonth() / 4);
}

/** First day of the season AFTER the one containing `d` (= when points refresh). */
function seasonEndDate(d: Date): Date {
  const startMonth = Math.floor(d.getUTCMonth() / 4) * 4 + 4;
  return new Date(Date.UTC(d.getUTCFullYear(), startMonth, 1, 12));
}

interface SeasonTally {
  steps: number;
  todayCount: number;
  aims: number;
  goals: number;
  challengeBonus: number;
  bypass: { day: number; week: number; goal: number };
  points: number;
  firstDate: Date | null;
  lastDate: Date | null;
  statFor: (wk: number) => WeekStat;
}

/** Full point tally for ONE season's log entries. `activePicks` = picks active
 *  at that season's level. `elapsedDays` caps DAY-bypass recovery. */
function tallySeason(
  dates: string[],
  log: Record<string, string[]>,
  activePicks: string[],
  perWeekOf: (id: string) => number,
  seed: string,
  elapsedDays: number | null,
): SeasonTally {
  let steps = 0;
  let todayCount = 0;
  const byPickWeek = new Map<string, Map<number, number>>();
  const weekTotal = new Map<number, number>();
  const weekDays = new Map<number, Set<number>>();
  let firstDate: Date | null = null;
  let lastDate: Date | null = null;

  for (const dateStr of dates) {
    const ids = log[dateStr];
    if (!Array.isArray(ids) || ids.length === 0) continue;
    const d = parseDate(dateStr);
    if (!d) continue;
    if (!firstDate) firstDate = d;
    lastDate = d;
    todayCount += 1;
    const wk = weekOrdinal(d);
    weekTotal.set(wk, (weekTotal.get(wk) ?? 0) + ids.length);
    let days = weekDays.get(wk);
    if (!days) {
      days = new Set();
      weekDays.set(wk, days);
    }
    days.add(d.getTime());
    for (const id of ids) {
      steps += 1;
      let weeks = byPickWeek.get(id);
      if (!weeks) {
        weeks = new Map();
        byPickWeek.set(id, weeks);
      }
      weeks.set(wk, (weeks.get(wk) ?? 0) + 1);
    }
  }

  const statFor = (wk: number): WeekStat => {
    const allMet =
      activePicks.length > 0 &&
      activePicks.every((p) => (byPickWeek.get(p)?.get(wk) ?? 0) >= perWeekOf(p));
    return { total: weekTotal.get(wk) ?? 0, days: weekDays.get(wk)?.size ?? 0, picksCount: activePicks.length, allMet };
  };

  // Challenges (daily + weekly + tiered bypass specials), within this season.
  let challengeBonus = 0;
  const earned = { day: 0, week: 0, goal: 0 };
  for (const dateStr of dates) {
    const dayIds = log[dateStr];
    if (!Array.isArray(dayIds) || dayIds.length === 0) continue;
    const def = DAILY_CHALLENGES[hashStr(`d:${dateStr}:${seed}`) % DAILY_CHALLENGES.length];
    if (def.test(dayIds, activePicks)) challengeBonus += def.bonus;
  }
  for (const wk of weekTotal.keys()) {
    const stat = statFor(wk);
    const def = WEEKLY_CHALLENGES[hashStr(`w:${wk}:${seed}`) % WEEKLY_CHALLENGES.length];
    if (def.test(stat)) challengeBonus += def.bonus;
    const tier = offeredTier(wk, seed);
    if (tier && SPECIALS[tier].test(stat)) earned[tier] += 1;
  }

  // AIM/GOAL with WEEK/GOAL bypasses.
  const bypass = { ...earned };
  const { aims, goals } = aimsGoals(byPickWeek, perWeekOf, bypass);

  // DAY bypass: recover idle days, capped by the season's elapsed span.
  const span =
    elapsedDays ?? (firstDate && lastDate ? Math.max(1, daysBetween(firstDate, lastDate) + 1) : todayCount);
  const recovered = Math.min(bypass.day, Math.max(0, span - todayCount));
  bypass.day -= recovered;
  const effTodayCount = todayCount + recovered;

  const points =
    steps * PTS.STEP + effTodayCount * PTS.TODAY + aims * PTS.AIM + goals * PTS.GOAL + challengeBonus;

  return { steps, todayCount: effTodayCount, aims, goals, challengeBonus, bypass, points, firstDate, lastDate, statFor };
}

const EMPTY_STAT_FOR = (): WeekStat => ({ total: 0, days: 0, picksCount: 0, allMet: false });

/**
 * Compute all acquisition stats for one deity from its progress log.
 *
 * SEASONS: points live inside a 4-month season (Jan–Apr / May–Aug / Sep–Dec).
 * Each season is tallied on its own; when a season crosses a level threshold,
 * that level is BANKED forever. At season end, un-banked progress toward the
 * next level refreshes — the next season starts from the last banked floor and
 * must earn the level's chunk (e.g. L2's 300) within one season.
 */
export function computeProgress(
  progress: DeityProgress | undefined,
  data: { levelIds: string[][]; perWeek: Record<string, number> },
  todayStr: string,
  seed = '',
): ProgressStats {
  const log = progress?.log ?? {};
  const picks = progress?.picks ?? [];
  const allDates = Object.keys(log).sort();
  const perWeekOf = (id: string) => data.perWeek[id] ?? Infinity;
  const today = parseDate(todayStr);
  const nowSeason = today ? seasonOf(today) : 0;

  // Group log dates into seasons, ascending.
  const bySeason = new Map<number, string[]>();
  for (const dateStr of allDates) {
    const d = parseDate(dateStr);
    if (!d) continue;
    const s = seasonOf(d);
    const arr = bySeason.get(s);
    if (arr) arr.push(dateStr);
    else bySeason.set(s, [dateStr]);
  }
  const seasonKeys = Array.from(bySeason.keys()).sort((a, b) => a - b);

  // Fold seasons: banked levels stick; in-progress points refresh each season.
  let bankedIdx = 0; // count of fully-banked LEVEL_THRESHOLDS
  let cur: SeasonTally | null = null;
  for (const s of seasonKeys) {
    if (s > nowSeason) break;
    const startLevel = bankedIdx + 1;
    const ids = data.levelIds[Math.min(startLevel - 1, data.levelIds.length - 1)] ?? [];
    const seasonPicks = picks.filter((p) => ids.includes(p));
    const isCurrent = s === nowSeason;
    const elapsed =
      isCurrent && today
        ? (() => {
            const first = bySeason.get(s)!.map(parseDate).find(Boolean) as Date | undefined;
            return first ? Math.max(1, daysBetween(first, today) + 1) : null;
          })()
        : null;
    const t = tallySeason(bySeason.get(s)!, log, seasonPicks, perWeekOf, seed, elapsed);
    const floor = bankedIdx > 0 ? LEVEL_THRESHOLDS[bankedIdx - 1] : 0;
    const working = floor + t.points;
    while (bankedIdx < LEVEL_THRESHOLDS.length && working >= LEVEL_THRESHOLDS[bankedIdx]) bankedIdx += 1;
    if (isCurrent) cur = t;
  }

  // Current-season working numbers (a season with no logs yet = zero tally).
  const activeIds = data.levelIds[Math.min(bankedIdx, data.levelIds.length - 1)] ?? [];
  const activePicks = picks.filter((p) => activeIds.includes(p));
  const bankedFloor = bankedIdx > 0 ? LEVEL_THRESHOLDS[bankedIdx - 1] : 0;
  const points = bankedFloor + (cur?.points ?? 0);
  const maxed = bankedIdx >= LEVEL_THRESHOLDS.length;
  const level = maxed ? LEVEL_THRESHOLDS.length : bankedIdx + 1;
  const levelFloor = bankedFloor;
  const levelTarget = LEVEL_THRESHOLDS[Math.min(bankedIdx, LEVEL_THRESHOLDS.length - 1)];
  const secured = bankedIdx >= 1 || points >= ACQUIRE_POINTS;
  const pct = maxed ? 1 : Math.max(0, Math.min(1, (points - levelFloor) / (levelTarget - levelFloor)));

  const steps = cur?.steps ?? 0;
  const aims = cur?.aims ?? 0;
  const goals = cur?.goals ?? 0;
  const effTodayCount = cur?.todayCount ?? 0;
  const challengeBonus = cur?.challengeBonus ?? 0;
  const bypass = cur?.bypass ?? { day: 0, week: 0, goal: 0 };
  const statFor = cur?.statFor ?? EMPTY_STAT_FOR;
  const tierIdx = secured ? 4 : goals > 0 ? 3 : aims > 0 ? 2 : effTodayCount > 0 ? 1 : 0;

  // Rate from the current season only; the projection must land inside the
  // season (points refresh at its end), so cap the date at season end.
  let ratePerDay = 0;
  let projectedDate: string | null = null;
  let seasonEnd: string | null = null;
  if (today) {
    const endD = seasonEndDate(today);
    seasonEnd = fmtDate(endD, endD.getUTCFullYear() !== today.getUTCFullYear());
    const first = cur?.firstDate ?? null;
    const elapsed = first ? Math.max(1, daysBetween(first, today) + 1) : 0;
    ratePerDay = elapsed > 0 ? (cur?.points ?? 0) / elapsed : 0;
    if (maxed) {
      projectedDate = 'maxed';
    } else if (ratePerDay > 0) {
      const daysLeft = Math.ceil((levelTarget - points) / ratePerDay);
      const target = new Date(today.getTime() + daysLeft * MS_DAY);
      projectedDate =
        target.getTime() > endD.getTime()
          ? `past season — resets ${seasonEnd}`
          : fmtDate(target, target.getUTCFullYear() !== today.getUTCFullYear());
    }
  }

  // ── Current-period challenge states for display.
  const nowWk = today ? weekOrdinal(today) : 0;
  const doneToday = Array.isArray(log[todayStr]) ? log[todayStr] : [];

  let todayChallenge: ChallengeState | null = null;
  let weekChallenge: ChallengeState | null = null;
  let specialChallenge: ChallengeState | null = null;
  if (activePicks.length > 0) {
    const dDef = DAILY_CHALLENGES[hashStr(`d:${todayStr}:${seed}`) % DAILY_CHALLENGES.length];
    todayChallenge = { label: dDef.label, detail: dDef.detail, bonus: dDef.bonus, met: dDef.test(doneToday, activePicks) };

    const wStat = statFor(nowWk);
    const wDef = WEEKLY_CHALLENGES[hashStr(`w:${nowWk}:${seed}`) % WEEKLY_CHALLENGES.length];
    weekChallenge = { label: wDef.label, detail: wDef.detail, bonus: wDef.bonus, met: wDef.test(wStat) };

    const tier = offeredTier(nowWk, seed);
    if (tier) {
      const s = SPECIALS[tier];
      specialChallenge = {
        label: `${s.icon} ${s.label}`,
        detail: s.detail,
        bonus: 0,
        met: s.test(wStat),
        grantsBypass: true,
      };
    }
  }

  return {
    points,
    steps,
    aims,
    goals,
    todayCount: effTodayCount,
    tierIdx,
    secured,
    level,
    levelTarget,
    levelFloor,
    maxed,
    pct,
    ratePerDay,
    projectedDate,
    seasonEnd,
    doneToday,
    activePicks,
    challengeBonus,
    bypass,
    todayChallenge,
    weekChallenge,
    specialChallenge,
  };
}

/** Picks lock for the week — only editable on Monday (device local). */
export function isPickDay(): boolean {
  return new Date().getDay() === 1; // 0 Sun … 1 Mon
}

/** Local 'YYYY-MM-DD' for today (device local time). */
export function todayKey(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
